import {
    cacheKeys,
    useCurrentWorkspace,
    useCustomMutation,
    useCustomQuery,
    useUpdateAsset,
    useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
    Colors,
    SaveFormTop,
    TopModalBar,
    useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { ProductAddonsAssignList } from '../../products/components/productAddonsAssignList';
import '../../shared/style/reactTabStyle.css';
import { AssetRoutineSearchInput } from './assetRoutineSearchInput';
import { EditAssetRoutinesList } from './editAssetRoutinesList';

// GraphQL queries defined inline
const FIND_ASSET_ROUTINES = `
  query FindAssetRoutines($assetId: String!) {
    asset {
      findAssetRoutines(assetId: $assetId) {
        routines
      }
    }
  }
`;

const ASSIGN_ROUTINES_TO_ASSET = `
  mutation Asset($input: AssignToAssetInput!) {
    asset {
      assignToAsset(input: $input) {
        message
        success
      }
    }
  }
`;

type Props = {
  assetId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'routines' | 'addons';
};

const getTabIndex = (tab?: 'routines' | 'addons') =>
  tab === 'addons' ? 1 : 0;

export const AssetRoutinesForm = ({
  assetId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const tableResultsBlockHeight = 50;
  const listMaxHeight = height - tableResultsBlockHeight;

  const [originalRoutineAssignments, setOriginalRoutineAssignments] = useState<
    string[] | undefined
  >();
  const [routineAssignments, setRoutineAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [originalAddonAssignments, setOriginalAddonAssignments] = useState<
    string[] | undefined
  >();
  const [addonAssignments, setAddonAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [keyword, setKeyword] = useState('');
  const [tabIndex, setTabIndex] = useState(() => getTabIndex(initialTab));

  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const asset = workspaceAsset?.asset;
  const productId = asset?.product?.id ?? '';
  const { updateAsset } = useUpdateAsset({ assetId });

  const { data: assignments, isPending: isAssignmentsLoading } = useCustomQuery(
    {
      query: FIND_ASSET_ROUTINES,
      cacheKey: [],
      variables: {
        assetId: assetId,
      },
    }
  );

  const { mutateAsync: assignRoutinesToAsset, isPending: isRoutinesLoading } =
    useCustomMutation({
      query: ASSIGN_ROUTINES_TO_ASSET,
    });

  const currentWorkspace = useCurrentWorkspace();

  useEffect(() => {
    if (assignments && !isAssignmentsLoading) {
      const routines = assignments.asset.findAssetRoutines.routines;
      setOriginalRoutineAssignments(routines);
      setRoutineAssignments(routines);
    }
  }, [
    assignments?.asset?.findAssetRoutines?.routines,
    isAssignmentsLoading,
  ]);

  useEffect(() => {
    const addons = asset?.addons ?? [];
    const addonIds = addons
      .filter((a) => a != null)
      .map((a: any) => (typeof a === 'object' && a?.id != null ? a.id : String(a)))
      .filter(Boolean) as string[];
    setOriginalAddonAssignments(addonIds);
    setAddonAssignments(addonIds);
  }, [asset?.addons]);

  useEffect(() => {
    if (initialTab) setTabIndex(getTabIndex(initialTab));
  }, [initialTab, assetId]);

  const isAddonsDirty = () =>
    originalAddonAssignments &&
    addonAssignments &&
    !(
      originalAddonAssignments.length === addonAssignments.length &&
      originalAddonAssignments.every((id) => addonAssignments.includes(id)) &&
      addonAssignments.every((id) => originalAddonAssignments.includes(id))
    );

  const handleRoutinesSubmit = async () => {
    const input = {
      routineIds: routineAssignments,
      assetId,
      workspaceId: currentWorkspace.id,
    };

    await assignRoutinesToAsset(
      { input: input },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('asset').all,
          });
        },
      }
    );

    onComplete();
  };

  const handleAddonsSubmit = async () => {
    await (updateAsset as any)(
      {
        input: {
          id: assetId,
          name: asset?.name ?? '',
          addons: addonAssignments || [],
          groupIds: workspaceAsset?.groups?.map((g: any) => g.id) || [],
        },
      },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('workspaceAsset').all,
          });
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('asset').all,
          });
        },
      }
    );
    onComplete();
  };

  const isRoutinesDirty = () =>
    originalRoutineAssignments &&
    routineAssignments &&
    !(
      originalRoutineAssignments.length === routineAssignments.length &&
      originalRoutineAssignments.every((item) =>
        routineAssignments.includes(item)
      ) &&
      routineAssignments.every((item) =>
        originalRoutineAssignments.includes(item)
      )
    );

  const isDirty = () =>
    tabIndex === 0 ? isRoutinesDirty() : isAddonsDirty();

  const handleSubmit = () =>
    tabIndex === 0 ? handleRoutinesSubmit() : handleAddonsSubmit();

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      <View style={styles.container}>
        <AssetRoutineSearchInput
          keyword={keyword}
          onChange={setKeyword}
          placeholder={
            tabIndex === 0
              ? 'assets.routineSearchPlaceholder'
              : 'assets.addonsSearchPlaceholder'
          }
        />

        <View style={[styles.assignmentContainer]} onLayout={onLayout}>
          <Tabs
            selectedIndex={tabIndex}
            onSelect={(index) => {
              setTabIndex(index);
            }}
          >
            <TabList>
              <Tab>{t('assets.routines')}</Tab>
              <Tab>{t('addOn.list.addOns')}</Tab>
            </TabList>

            <View style={styles.listContainer}>
              <TabPanel>
                {routineAssignments && currentWorkspace?.id && (
                  <EditAssetRoutinesList
                    workspaceId={currentWorkspace.id}
                    assetId={assetId}
                    maxHeight={listMaxHeight}
                    keyword={keyword}
                    assignments={routineAssignments}
                    updateAssignments={setRoutineAssignments}
                  />
                )}
              </TabPanel>
              <TabPanel>
                {currentWorkspace?.id && productId && (
                  <ProductAddonsAssignList
                    workspaceId={currentWorkspace.id}
                    productId={productId}
                    maxHeight={listMaxHeight}
                    keyword={keyword}
                    assignments={addonAssignments ?? []}
                    updateAssignments={setAddonAssignments}
                  />
                )}
              </TabPanel>
            </View>
          </Tabs>
        </View>
      </View>
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
  inputStyle: {
    backgroundColor: Colors.white,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    borderRadius: 8,
  },
}); 